import './RemoteSearchInput.pcss';
import React, {
  ChangeEventHandler,
  KeyboardEventHandler,
  FocusEventHandler,
  FocusEvent,
  useCallback,
  useState,
  ReactNode,
} from 'react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import Input from '../Input/Input';
import { OnClickElsewhere } from '../OnClickElsewhere/OnClickElsewhere';
import Spinner from '../Spinner';
import { SearchOutlined } from '@ant-design/icons';

type HTMLElementTypes = HTMLInputElement | HTMLTextAreaElement;

const DEFAULT_DEBOUNCE = 500;

export type Props<T> = {
  value: string | undefined;
  results: T[];
  debounceOptions?: { leading?: boolean; trailing?: boolean; maxWait?: number };
  renderSuggestion: (suggestion: T, query: string) => ReactNode;
  onSelectedItem: (selectedItem: T) => void;
  lengthLimit?: number;
  selectorClass?: string;
  onChange?: ChangeEventHandler<HTMLElementTypes>;
  onBlur?: (e?: FocusEvent<HTMLElementTypes>) => void;
  onFocus?: FocusEventHandler<HTMLElementTypes>;
  onKeyDown?: KeyboardEventHandler<HTMLElementTypes>;
  onFetch: (query: string) => void;
  inputRef?: (ref: HTMLElementTypes) => void;
  debounceTimeout?: number;
  disabled?: boolean;
  style?: { [key: string]: any };
  className?: string;
  withSearchIcon?: boolean;
  clearInputOnSelect?: boolean;
  [key: string]: any;
};

const getStyles = (
  error: boolean,
  disabled: boolean | undefined,
  style = {},
) => {
  const inputStyles = {
    width: '100%',
    height: '100%',
    backgroundColor: disabled ? 'inherit' : 'white',
    ...style,
  };

  if (error) {
    return {
      ...inputStyles,
      border: '2px solid rgb(215, 117, 121)',
    };
  }

  return inputStyles;
};

export const RemoteSearchInput = <T,>(props: Props<T>) => {
  const {
    style,
    error,
    disabled,
    debounceTimeout,
    value,
    onFetch,
    debounceOptions,
    renderSuggestion,
    selectorClass,
    onSelectedItem,
    lengthLimit,
    results,
    onFocus,
    withSearchIcon,
    clearInputOnSelect,
    ...inputProps
  } = props;

  // init variables
  const timeout =
    debounceTimeout === undefined ? DEFAULT_DEBOUNCE : debounceTimeout;
  const inputStyle = getStyles(error, disabled, style);
  const selectorClasses = classnames('remote-search-input__selector', {
    selectorClass: !!selectorClass,
  });

  // state
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpened, setIsOpened] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  // callbacks
  const fetch = useCallback(
    debounce(
      async (value: string) => {
        if (!value || (lengthLimit && value.length < lengthLimit)) return;
        setIsOpened(false);
        setIsRequesting(true);
        await onFetch(value);
        setIsOpened(true);
        setIsRequesting(false);
      },
      timeout,
      { trailing: true, ...debounceOptions },
    ),
    [timeout, onFetch],
  );

  const onChange: typeof props.onChange = useCallback(
    event => {
      const newValue = event.target.value;
      setIsOpened(false);
      setInputValue(newValue);
      fetch(newValue);
      if (props.onChange) {
        props.onChange(event);
      }
    },
    [props.onChange, fetch],
  );

  const handleClickOutside = useCallback(() => {
    setIsOpened(false);
  }, []);

  const handleSelected = useCallback(
    (selectedItem: any) => {
      setIsOpened(false);
      onSelectedItem(selectedItem);
      if (clearInputOnSelect) {
        setInputValue('');
      }
    },
    [onSelectedItem, clearInputOnSelect],
  );

  const onInputFocus = useCallback(
    (event: FocusEvent<HTMLElementTypes>) => {
      if (onFocus) {
        onFocus(event);
      }
      if (disabled) {
        return;
      }
      setIsOpened(true);
    },
    [onFocus],
  );

  // render
  return (
    <div className="remote-search-input__container">
      <OnClickElsewhere
        className={selectorClasses}
        handleClick={handleClickOutside}
      >
        {withSearchIcon && (
          <SearchOutlined
            className={classnames('remote-search-input__serach-icon', {
              'remote-search-input__serach-icon-disabled': disabled,
            })}
          />
        )}
        <Input
          value={inputValue}
          onFocus={onInputFocus}
          disabled={disabled}
          style={inputStyle}
          onChange={onChange}
          {...inputProps}
        />
        <Spinner
          className="remote-search-input__spinner"
          show={isRequesting}
          position="absolute"
          backgroundColor="none"
          size={15}
        />
        {results.length > 0 && isOpened && (
          <div className="remote-search-input__dropdown">
            {results.map((result, index) => (
              <div
                className="remote-search-input__suggestion"
                key={index}
                onClick={() => handleSelected(result)}
              >
                {renderSuggestion(result, inputValue)}
              </div>
            ))}
          </div>
        )}
      </OnClickElsewhere>
    </div>
  );
};
